import React, { useState, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import selectors from '@state/selectors';
import {IProps, IOEMDefinitionState} from './types';
import * as S from './styles';
import C from '@assets/themes/colors';
import { Context } from "routes/private/Editor/Context";
import { IBrandDefinition, IBrandConfigFont } from '@state/dsconfig/types';
import { IDSConfig } from '@state/dsconfig/types';
import { findOEM, findElementById, getOEMName, getBrandIdByOEMName } from '@utils/editor';
import { ArrowDownIcon } from '@assets/icons/arrowDownIcon';
import { IElement, ITextAttributes } from '@routes/private/Editor/types';
import DragElement from '../DragElement';
import { findFontByNameOrId, findOEMByNameOrId, findFontsDefinition } from '@utils/fonts';
import SelectDownIcon from '@assets/icons/selectDownIcon';
import RemoveIcon from '@assets/icons/removeIcon';
import { cloneDeep } from 'lodash';
import { kMaxLength } from 'buffer';
import { updateRenderingTextSegments } from 'utils/textMultiStyling'
const uuid = require('react-uuid');

const NewTextPicker:React.FC<IProps> = (props) => {
  let context = useContext(Context);
  const dsConfig: IDSConfig = useSelector(selectors.dsConfig.getDSConfig);
  const [oemDefinitions, setOemDefinitions] = useState<IOEMDefinitionState[]>([]);
  const oem = findOEM(context.entityInformation);
  
  const TEXT_DIMENSIONS = dsConfig.editorConfig.newElementSettings.textSizes ?? {width: 250, height: 15};
  const TEXT_COLOR = dsConfig.editorConfig.newElementSettings.textDefaultColor ?? C.DARK_BLUE;
  
  const [lastFontFamily, setLastFontFamily]= useState<IBrandConfigFont | undefined>(undefined);
  const [searchValue, setSearchValue]= useState("");
  const [fontsFound,setFontsFound] = useState<IBrandConfigFont[]>([]);
  const searchInputRef = React.useRef() as React.MutableRefObject<HTMLInputElement>;
  const pageSize: number|undefined = 30;
  let fontsCounter = 0;
  const [resultsMaxLength,setResultsMaxLength] = useState<number|undefined>(pageSize);

  const scrollEnd = (e: React.UIEvent<HTMLElement>): boolean => {
    const optionsContainer = (e.target as HTMLDivElement);
    return (optionsContainer.offsetHeight + optionsContainer.scrollTop >= optionsContainer.scrollHeight)
  }

  const optionsOnScroll = (e: React.UIEvent<HTMLElement>) => {
      if (pageSize && resultsMaxLength) {
          if (scrollEnd(e)) {
              setResultsMaxLength(resultsMaxLength! + pageSize);
          }
      }
  }

  useEffect(() => {
    setResultsMaxLength(pageSize);
    if (searchValue!=="") {
      let joinedFontsLists: IBrandConfigFont[] = [];
      for(let i=0;i<oemDefinitions.length;i++) {
        if (oemDefinitions[i].fonts) {
          joinedFontsLists = joinedFontsLists.concat(oemDefinitions[i].fonts)
        }
      }
      const matchedFonst = joinedFontsLists.filter(font => (font.name.toUpperCase().indexOf(searchValue!.toUpperCase())!==-1)).sort((a, b)=>{return a.name.toLowerCase().localeCompare(b.name.toLowerCase());});
      setFontsFound(matchedFonst);
    }
    else {
        setFontsFound([]);
    }
  }, [oemDefinitions,searchValue]);

  const handleChangeSearchInput = (e: any) => {
    const text = (e.target as HTMLInputElement).value;
    if(lastFontFamily?.name !== text) {
      setSearchValue(text);
      const font = findFontByNameOrId(dsConfig.brands, "name", text);
      if (font) setLastFontFamily(font);
    } 
  }

  const filterFontsForMaxLenght = (fonts: IBrandConfigFont[]) => {
    const filteredFonts = cloneDeep(fonts);
    if (filteredFonts && resultsMaxLength && resultsMaxLength<filteredFonts.length) {
      return filteredFonts.splice(0,resultsMaxLength);
    }
    return filteredFonts; 
  }

  useEffect(() => {
    if (dsConfig) {
      let oemId = oem;
      if (findOEMByNameOrId(dsConfig, "id", oem ?? "")?.genericOrOther === true) oemId = undefined;
      const oemFontsDefinition: IOEMDefinitionState[] = findFontsDefinition(dsConfig.brands, oemId).map(oemFontDefinition => {return {
        ...oemFontDefinition,
        expand: false
      }});
      if (findOEMByNameOrId(dsConfig, "id", oem ?? "")?.genericOrOther === true) oemId = undefined;
      const notoemFontsDefinition: IOEMDefinitionState[] = findFontsDefinition(dsConfig.brands, undefined, true).map(oemFontDefinition => {return {
        ...oemFontDefinition,
        expand: false
      }});
      const commonFontsDefinition: IOEMDefinitionState[] = findFontsDefinition(dsConfig.brands, "common").map(oemFontDefinition => {return {
        ...oemFontDefinition,
        expand: true
      }});
      setOemDefinitions([...oemFontsDefinition, ...notoemFontsDefinition, ...commonFontsDefinition]);
    }
  }, [dsConfig]);

  useEffect(() => {
    const draggingData = context.editorStatus.draggingData;
    if (draggingData?.type === "Text") {
      handleAddDropText(draggingData.id, draggingData.containerId, draggingData.coorsPercentage, draggingData.elementCoorsPercentage);
    }
  }, [context.editorStatus.draggingData]);

  const handleAddDropText = (font: string, id?: string, coors?: {x: number, y: number}, elementCoors?: {x: number, y: number}) => {
    const oemFont = findFontByNameOrId(oemDefinitions, "id", font);
    if (oemFont) {
      const selectedArtboards: string[] = context.editorStatus.selectedArtboardIds ? context.editorStatus.selectedArtboardIds : [];
      const idIndex = selectedArtboards.findIndex((artBoardId: string) => (artBoardId === id));
      if (id) {
        if (idIndex >= 0) {
          if (selectedArtboards.length === 1) handleAddText([id], oemFont, coors, elementCoors);
          else handleAddTextManager(oemFont);
          
        } else {
          context.onSelectedArtboardChange(id);
          handleAddText([id], oemFont, coors, elementCoors);
        }
      } else {
        handleAddText([context.data[0].id], oemFont, coors, elementCoors);
      }
    }
  }

  const handleAddTextManager = (font: IBrandConfigFont) => {
    let ids: string[] = [];
    if (context.data[0].type === "Artboards") {
      if (context.editorStatus.selectedArtboardIds && context.editorStatus.selectedArtboardIds.length > 0) {
        context.editorStatus.selectedArtboardIds.map((id: string) => {
          ids = [...ids, id];
        });
      } else if (context.data[0]?.children && context.data[0]?.children.length > 0) {
        context.data[0].children.map((child: IElement) => {
          ids = [...ids, child.id];
        });
      }
      handleAddText(ids, font);
    } else {
      handleAddText([context.data[0].id], font);
    }
  }

  const handleAddText = (ids: string[], font: IBrandConfigFont, coors?: {x: number, y: number}, elementCoors?: {x: number, y: number}) => {
    let newTextElements: IElement[] = [];
    ids.map(id => {
      let parentElement: IElement | undefined = findElementById(context.data, id);
      if (parentElement === undefined) parentElement = context.data[0];
      let coorX = parentElement.attributes.x;
      let coorY = parentElement.attributes.y;
      if (coors && elementCoors) {
        coorX += coors.x * parentElement.attributes.width - elementCoors.x * 208 + 8;
        coorY += coors.y * parentElement.attributes.height - elementCoors.y * 35 + 8;
      } else {
        coorX += parentElement.attributes.width/2 - TEXT_DIMENSIONS.width/2;
        coorY += parentElement.attributes.height/2 - TEXT_DIMENSIONS.height/2;
      }
      const newAtributes: ITextAttributes = {
        x: coorX,
        y: coorY,
        width: coors ? 208 : TEXT_DIMENSIONS.width,
        height: TEXT_DIMENSIONS.height,
        hidden: false,
        text: font.name,
        textAlign: coors? 'left' : 'center',
        opacity: 1,
        style:{fontItalic: false, fontWeight: false, fontFamily: font.id, fontSize: 14, fontUnderline: false, color: TEXT_COLOR, lineHeight: 1.2, letterSpacing: 0 },
        parts:[{length: font.name.length,style: {}}],
        partsForRendering: []
      };
      const newTextElement: IElement = {
        id: uuid(),
        name: font.name,
        type: "Text",
        attributes: updateRenderingTextSegments(newAtributes),
      };
      newTextElements = [...newTextElements, newTextElement];
    })
    context.onCommandChange("addElement", undefined, ids, newTextElements);
  }

  const handleExpandOEM = (index: number) => {
    setOemDefinitions([
      ...oemDefinitions.slice(0, index),
      {...oemDefinitions[index], expand: !oemDefinitions[index].expand},
      ...oemDefinitions.slice(index + 1)
    ]);
  }

  const OEMFonts = (oemDefinition: IOEMDefinitionState) => {
    const oemFonts = oemDefinition.expand ? (oemDefinition.name==='Common' ? filterFontsForMaxLenght(oemDefinition.fonts) : oemDefinition.fonts) : oemDefinition.fonts.slice(0, 3);
    return (
      <S.OEMFontsContainer
        maxHeightValue={oemDefinition.expand ? '10000px' : '200px'}
      >
        { oemFonts.length > 0 &&
          oemFonts.map((font, index: number) => {
            fontsCounter++;
            if (resultsMaxLength && fontsCounter<resultsMaxLength)
              return (
                <DragElement
                  key={index}
                  id={font.id}
                  type="Text"
                  style={{margin: `0px 0px 8px 0px`}}
                >
                  <S.OEMFont 
                    onClick={() => handleAddTextManager(font)}              
                  >
                    <S.DragFontContainer
                      role="NewText"
                      fontFamilyValue={`font-${font.id}`}
                    >
                      {font.name}
                    </S.DragFontContainer>
                  </S.OEMFont>
                </DragElement>)
            else return;
          })
        }
      </S.OEMFontsContainer>
    )
  }

  return (
    <S.Root
        onScroll={(e:any)=>optionsOnScroll(e)}
    >
    <S.TopBottomSpace/>
    <S.SearchContainer>
        <S.SearchInput 
          ref={searchInputRef}
          placeholder="Search Fonts"
          onKeyUp={(e) => handleChangeSearchInput(e)}
          contentEditable={true}>
        </S.SearchInput>
        {
          searchInputRef.current && searchInputRef.current.value!=="" && 
          <S.RemoveIconContainer onClick={() => {searchInputRef.current.value=""; setSearchValue("");}}>
            <RemoveIcon/>
          </S.RemoveIconContainer>
        }
      </S.SearchContainer>
      {
        searchValue==="" && 
        <>
        { oemDefinitions.length > 0 &&
          <S.HelpText>
            Click text to add to page
          </S.HelpText>
        }
        <S.FontsContainer>
        { oemDefinitions.length > 0 &&
          oemDefinitions.map((oemDefinition, index: number) => {
            if (resultsMaxLength && fontsCounter<resultsMaxLength && oemDefinition.fonts?.length > 0) 
              return (
                <S.OEMContainer key={index} >
                  <S.OEMTitleContainer>
                    <S.OEMTitle>
                      {oemDefinition.name} Fonts
                    </S.OEMTitle>
                    { oemDefinition.fonts?.length > 3 &&
                      <S.OEMExpand
                        onClick={() => handleExpandOEM(index)}
                        rotationValue={oemDefinition.expand ? '-180' : '0'}
                      >
                        <ArrowDownIcon style={{fillColor: C.DARK_BLUE}} />
                      </S.OEMExpand>
                    } 
                  </S.OEMTitleContainer>
                  {OEMFonts(oemDefinition)}
                </S.OEMContainer>
              )
            else return;
          })
        }
        </S.FontsContainer>
        </>
      }
      {
        searchValue!=="" &&
        <>
        { 
          fontsFound.length>0 &&
          <S.HelpText>
            Click text to add to page
          </S.HelpText>
        }
        {
            fontsFound.length>0 && 
            filterFontsForMaxLenght(fontsFound).map((fontFound, index) => {
              return (
                <DragElement
                    key={index}
                    id={fontFound.id}
                    type="Text"
                    style={{margin: `0px 0px 8px 0px`}}
                >
                    <S.OEMFont 
                    onClick={() => handleAddTextManager(fontFound)}              
                    >
                    <S.DragFontContainer
                        role="NewText"
                        fontFamilyValue={`font-${fontFound.id}`}
                    >
                        {fontFound.name}
                    </S.DragFontContainer>
                    </S.OEMFont>
                </DragElement>
              )
            })
        }
        {
            fontsFound.length===0 && 
            <S.Message>
                No results found for this font 
            </S.Message>
        }
        </>
    }
    <S.TopBottomSpace/>
    </S.Root>
  );
}

export default NewTextPicker;